// src/features/booking/NumberingSchemeDialog.tsx
// Create / update a booking numbering scheme (admin).

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow, SegmentedControl } from '@/components/form';
import { errMessage } from './lib';
import { CreateBookingNumberingScheme } from '@core/api/endpoints/booking/create_booking_numbering_scheme';
import { UpdateBookingNumberingScheme } from '@core/api/endpoints/booking/update_booking_numbering_scheme';
import type { BookingNumberingPattern, BookingNumberingResetStrategy, BookingNumberingScheme } from '@core/types/booking';

interface Props {
  open: boolean;
  onClose: () => void;
  scheme: BookingNumberingScheme | null;
  scope: { productId: string; platformId: string; tenantId: string };
  onSaved: () => void;
}

const PATTERNS: BookingNumberingPattern[] = ['BOOKING_NUMBERING_PATTERN_SEQUENTIAL', 'BOOKING_NUMBERING_PATTERN_YEAR_SEQUENTIAL', 'BOOKING_NUMBERING_PATTERN_CUSTOM'];
const RESETS: BookingNumberingResetStrategy[] = ['BOOKING_NUMBERING_RESET_STRATEGY_NEVER', 'BOOKING_NUMBERING_RESET_STRATEGY_YEARLY', 'BOOKING_NUMBERING_RESET_STRATEGY_QUARTERLY', 'BOOKING_NUMBERING_RESET_STRATEGY_MONTHLY'];

function digits(v: string): string {
  return v.replace(/[^0-9]/g, '');
}

export function NumberingSchemeDialog({ open, onClose, scheme, scope, onSaved }: Props) {
  const { t } = useI18n();
  const isEdit = !!scheme;
  const descRef = useRef<HTMLTextAreaElement>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [prefix, setPrefix] = useState('BK');
  const [separator, setSeparator] = useState('-');
  const [pattern, setPattern] = useState<BookingNumberingPattern>('BOOKING_NUMBERING_PATTERN_YEAR_SEQUENTIAL');
  const [counterDigits, setCounterDigits] = useState('4');
  const [resetStrategy, setResetStrategy] = useState<BookingNumberingResetStrategy>('BOOKING_NUMBERING_RESET_STRATEGY_YEARLY');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (scheme) {
      setName(scheme.name ?? '');
      setDescription(scheme.description ?? '');
      setPrefix(scheme.prefix ?? 'BK');
      setSeparator(scheme.separator ?? '-');
      setPattern(scheme.pattern ?? 'BOOKING_NUMBERING_PATTERN_YEAR_SEQUENTIAL');
      setCounterDigits(String(scheme.counter_digits || 4));
      setResetStrategy(scheme.reset_strategy ?? 'BOOKING_NUMBERING_RESET_STRATEGY_YEARLY');
    } else {
      setName('');
      setDescription('');
      setPrefix('BK');
      setSeparator('-');
      setPattern('BOOKING_NUMBERING_PATTERN_YEAR_SEQUENTIAL');
      setCounterDigits('4');
      setResetStrategy('BOOKING_NUMBERING_RESET_STRATEGY_YEARLY');
    }
    setError(null);
  }, [open, scheme]);

  const patternOptions = PATTERNS.map((p) => ({ value: p, label: t(`booking.admin.numbering.pattern.${p.replace('BOOKING_NUMBERING_PATTERN_', '').toLowerCase()}`) }));
  const resetOptions = RESETS.map((r) => ({ value: r, label: t(`booking.admin.numbering.reset.${r.replace('BOOKING_NUMBERING_RESET_STRATEGY_', '').toLowerCase()}`) }));

  const dirty = !!(name.trim() || description.trim());

  const preview = `${prefix}${separator}2026${separator}${'1'.padStart(Number(counterDigits) || 4, '0')}`;

  const submit = async () => {
    if (!name.trim() || !prefix.trim()) {
      setError(t('booking.admin.numbering.requiredFields'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit && scheme?.entity) {
        await UpdateBookingNumberingScheme(scheme.entity.id, {
          scheme: { ...scheme, name, description, prefix, separator, pattern, counter_digits: parseInt(counterDigits || '4', 10), reset_strategy: resetStrategy },
          field_mask: ['name', 'description', 'prefix', 'separator', 'pattern', 'counter_digits', 'reset_strategy'],
        });
      } else {
        await CreateBookingNumberingScheme({
          entity: { type_id: '', type_name: 'booking_numbering_scheme', id: '', platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId },
          name,
          description,
          prefix,
          separator,
          pattern,
          counter_digits: parseInt(counterDigits || '4', 10),
          reset_strategy: resetStrategy,
        });
      }
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(errMessage(e, t('booking.admin.numbering.saveError')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={isEdit ? t('common.saveChanges') : t('booking.admin.numbering.createSubmit')}
      canSubmit={!!name.trim() && !!prefix.trim()}
      isSubmitting={saving}
      dirty={dirty}
      aria-label={isEdit ? t('booking.admin.numbering.editTitle') : t('booking.admin.numbering.createTitle')}
    >
      <div className="px-3.5 pb-1 pt-2">
        {error && (
          <div className="pb-2">
            <Alert variant="error">{error}</Alert>
          </div>
        )}
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={`${t('booking.admin.numbering.field.name')}…`}
          initialFocus
          onEnter={() => descRef.current?.focus()}
          aria-label={t('booking.admin.numbering.field.name')}
        />
        <AutoGrowTextarea
          ref={descRef}
          value={description}
          onChange={setDescription}
          placeholder={`${t('booking.admin.numbering.field.description')}…`}
          minRows={1}
        />
      </div>

      <div className="space-y-1 border-t border-border px-3.5 py-2">
        <FormRow label={t('booking.admin.numbering.preview')} labelWidth="sm">
          <span className="font-mono text-sm text-muted-foreground">{preview}</span>
        </FormRow>
        <FormRow label={t('booking.admin.numbering.field.prefix')} labelWidth="sm">
          <span className="inline-flex items-center gap-2">
            <input
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              aria-label={t('booking.admin.numbering.field.prefix')}
              className="w-20 rounded-md border border-input bg-background px-2 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              value={separator}
              onChange={(e) => setSeparator(e.target.value)}
              maxLength={2}
              aria-label={t('booking.admin.numbering.field.separator')}
              className="w-12 rounded-md border border-input bg-background px-2 py-1.5 text-center font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              type="text"
              inputMode="numeric"
              value={counterDigits}
              onChange={(e) => setCounterDigits(digits(e.target.value))}
              aria-label={t('booking.admin.numbering.field.counterDigits')}
              className="w-12 rounded-md border border-input bg-background px-2 py-1.5 text-right text-sm tabular-nums text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
            <span className="text-xs text-muted-foreground">{t('booking.admin.numbering.field.counterDigitsHint')}</span>
          </span>
        </FormRow>
        <FormRow label={t('booking.admin.numbering.field.pattern')} labelWidth="sm">
          <SegmentedControl size="sm" value={pattern} onChange={(v) => setPattern(v as BookingNumberingPattern)} options={patternOptions} aria-label={t('booking.admin.numbering.field.pattern')} />
        </FormRow>
        <FormRow label={t('booking.admin.numbering.field.resetStrategy')} labelWidth="sm">
          <SegmentedControl size="sm" value={resetStrategy} onChange={(v) => setResetStrategy(v as BookingNumberingResetStrategy)} options={resetOptions} aria-label={t('booking.admin.numbering.field.resetStrategy')} />
        </FormRow>
      </div>
    </FormDialog>
  );
}
